import React, { useContext, useState } from "react";
import DContext from "../../context/DContext";
import { ROLE_VISIBILITY, isVisibleForRole } from "../../config/siteConfig";
import moment from "moment";

const ACCENT = "var(--btn-bg)";

const formatTime = (t) => (t ? String(t).slice(0, 5) : "--:--");
const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export const getDurationMinutes = (fare) => {
  const dep = moment(`${fare.origin?.date} ${fare.origin?.time}`, "YYYY-MM-DD HH:mm:ss");
  const arr = moment(`${fare.destination?.date} ${fare.destination?.time}`, "YYYY-MM-DD HH:mm:ss");
  const mins = arr.diff(dep, "minutes");
  return Number.isFinite(mins) && mins > 0 ? mins : 0;
};

// Total price for the searched passengers (per-pax fares from the API x pax count)
export const getPaxTotal = (fare, pax) => {
  const f = fare.fare || {};
  return (
    (f.adult?.total_fare || 0) * (pax?.adults || 0) +
    (f.child?.total_fare || 0) * (pax?.children || 0) +
    (f.infant?.total_fare || 0) * (pax?.infants || 0)
  );
};

const formatDuration = (mins) => (mins ? `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m` : "-");

const DETAIL_TABS = [
  { value: "flight", label: "Flight Detail", icon: "bi-airplane" },
  { value: "baggage", label: "Baggage Details", icon: "bi-suitcase2" },
  { value: "cancellation", label: "Cancellation", icon: "bi-arrow-counterclockwise" },
  // { value: "itinerary", label: "Itinerary", icon: "bi-signpost-split" },
];

const FlightResultCard = ({ fare, pax, routeType: routeTypeProp, onBook }) => {
  const { authState } = useContext(DContext);
  const showBookNow = isVisibleForRole(ROLE_VISIBILITY.flightSearchPage.bookNowButton, authState);
  const [showDetails, setShowDetails] = useState(false);
  const [tab, setTab] = useState("flight");
  const [logoError, setLogoError] = useState(false);

  const travellersCount = (pax?.adults || 0) + (pax?.children || 0) + (pax?.infants || 0);
  const duration = getDurationMinutes(fare);
  const total = getPaxTotal(fare, pax);
  const stops = Number(fare.flight_stops || 0);
  const dayShift = moment(fare.destination?.date).diff(moment(fare.origin?.date), "days");

  const breakupRows = [
    { key: "adult", label: "Adult", count: pax?.adults || 1 },
    { key: "child", label: "Child", count: pax?.children || 1 },
    { key: "infant", label: "Infant", count: pax?.infants || 1 },
  ];

  // Total Fare Summary lists only the passenger types that were searched (count > 0)
  const selectedCounts = { adult: Number(pax?.adults ?? 1), child: Number(pax?.children ?? 0), infant: Number(pax?.infants ?? 0) };
  const summaryRows = breakupRows
    .map((r) => ({ ...r, count: selectedCounts[r.key] }))
    .filter((r) => r.count > 0);

  const seats = fare.available_seats;
  const lowSeats = seats != null && seats <= 9;
  const totalSeats = Number(fare.total_seats ?? seats ?? 0);
  const bookedSeats = Number(fare.booked_seats ?? Math.max(totalSeats - Number(seats || 0), 0));
  const bookedPct = totalSeats ? Math.min(100, Math.round((bookedSeats / totalSeats) * 100)) : 0;
  //const perPax = fare.fare?.adult?.total_fare;
  const totalFare=fare?.fare?.total?.total_fare;
  const routeType = (routeTypeProp || "domestic").toUpperCase();

  return (
    <div className="fr-card">
      {/* <span className={`fr-corner-tag ${fare.is_refundable ? "yes" : "no"}`}>
        <i className={`bi ${fare.is_refundable ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i>
        {fare.is_refundable ? "Refundable" : "Non Refundable, Non Changable"}
      </span> */}
      <div className="fr-row">
        {/* Airline */}
        <div className="fr-col fr-col-route">
          <div className="fr-airline">
            {fare.airline?.logo && !logoError ? (
              <img src={fare.airline.logo} alt={fare.airline?.airline_code} className="fr-logo" onError={() => setLogoError(true)} />
            ) : (
              <div className="fr-logo fr-logo-fallback">{fare.airline?.airline_code}</div>
            )}
            <div className="min-w-0">
              <div className="fr-airline-name text-truncate" title={fare.airline?.airline_name}>{fare.airline?.airline_name}</div>
              <div className="fr-muted text-truncate">{fare.airline?.airline_code} - {fare.flight_number}</div>
              <span className="fr-badge">{routeType}</span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="fr-col fr-col-flight">
          <div className="fr-timeline">
            <div>
              <div className="fr-date">{moment(fare.origin?.date).format("ddd, D MMM YYYY")}</div>
              <div className="fr-time">{formatTime(fare.origin?.time)}</div>
              {/* <div className="fr-muted text-truncate">{fare.origin?.city_name}{fare.origin?.terminal ? ` · T${fare.origin.terminal}` : ""}</div> */}
              {/* <div className="fr-muted-city text-truncate">{fare.origin?.city_name} ({fare.origin?.airport_code})</div>               */}
              <div className="fr-green-city"><span>{fare?.origin?.city_name}</span>
                    <span className="fr-muted-city text-truncate">
                      ({fare?.origin?.airport_code})
                    </span>
              </div>
              {/* <div  className="fr-muted-odshow"  title={fare.origin?.airport_name}>  {fare.origin?.airport_name?.length > 20 ? `${fare.origin.airport_name.slice(0, 20)}...` : fare.origin?.airport_name}</div> */}
              <div  className="fr-muted-odshow"  title={fare.origin?.airport_name}>{fare.origin?.airport_name}</div>
              <div className="fr-muted text-truncate"> {fare.origin?.terminal ? ` Terminal ${fare.origin.terminal}` : ""}</div>
            </div>
            <div className="fr-path">
              <div className="fr-muted">{formatDuration(duration)}</div>
              <div className="fr-line">
                <span className="fr-line-end start" />
                {stops > 0 && <span className="fr-line-stop" />}
                <span className="fr-line-end end" />
              </div>
              <div className="fr-stops" style={{ color: stops == 0 ? "#16a34a" : ACCENT }}>
                {stops == 0 ? "Non-stop" : `${stops} Stop${stops > 1 ? "s" : ""}`}
              </div>
              <div className="fr-muted text-capitalize">{fare.cabin_class}</div>
            </div>
            <div className="text-end">
              <div className="fr-date">{moment(fare.destination?.date).format("ddd, D MMM YYYY")}</div>
              <div className="fr-time">
                {formatTime(fare.destination?.time)}
                {dayShift > 0 && <sup className="fr-dayshift">+{dayShift}</sup>}
              </div>
              {/* <div className="fr-muted text-truncate">{fare.destination?.city_name}{fare.destination?.terminal ? ` · T${fare.destination.terminal}` : ""}</div> */}
              {/* <div className="fr-muted-city text-truncate">{fare?.destination?.city_name} ({fare?.destination?.airport_code})</div> */}
              <div className="fr-green-city"><span>{fare?.destination?.city_name}</span>
                    <span className="fr-muted-city text-truncate">
                      ({fare?.destination?.airport_code})
                    </span>
              </div>
              {/* <div  className="fr-muted-odshow"  title={fare.destination?.airport_name}>  {fare.destination?.airport_name?.length > 20 ? `${fare.destination.airport_name.slice(0, 20)}...` : fare.destination?.airport_name}</div> */}
              <div  className="fr-muted-odshow"  title={fare.destination?.airport_name}>  {fare.destination?.airport_name}</div>
              <div className="fr-muted text-truncate"> {fare.destination?.terminal ? ` Terminal ${fare.destination.terminal}` : ""}</div>
            </div>
          </div>
        </div>

        {/* Availability */}
        {/* <div className="fr-col fr-col-avail">
          <div className="fr-avail-title">{seats != null ? `${seats} Seat${seats == 1 ? "" : "s"} Available` : "Seats on request"}</div>
          <span className={`fr-avail-badge ${lowSeats ? "low" : ""}`}>
            <span className="fr-avail-dot" /> {lowSeats ? "Limited Seats" : "Seats Available"}
          </span>
          <div className={`fr-progress ${lowSeats ? "low" : ""}`}><span style={{ width: `${bookedPct || 100}%` }} /></div>
          <div className="fr-muted fr-seat-stats">
            <span>Total <b>{totalSeats}</b></span>
            <span>Available <b>{seats ?? 0}</b></span>
            <span>Booked <b>{bookedSeats}</b></span>
          </div>
        </div> */}

        {/* Fare */}
        <div className="fr-col fr-col-fare" title={total && travellersCount > 1 ? `Total for ${travellersCount} travellers: ${formatINR(total)}` : undefined}>
          {/* <div className="fr-fare-label">NET FARE <span className="fr-series-tag">SERIES FARE</span></div> */}
          {/* <div className="fr-price">{formatINR(perPax)}</div> */}
          <div className="fr-price">{fare?.fare_code}{totalFare}</div>
          {/* {fare.rbd && <span className="fr-badge fr-badge-outline text-capitalize">{fare.cabin_class} ({fare.rbd})</span>} */}
          {/* <div className="fr-muted">Per pax · Taxes included</div> */}
          <div className="fr-muted"><i class="bi bi-person-fill"></i>{" "}{fare?.supplier_custom_code ?? fare?.supplier_name}</div>
        </div>

        {/* Actions */}
        <div className="fr-col fr-col-actions">
          {showBookNow && (
            <button type="button" className="fr-book-btn" onClick={() => onBook?.(fare)}>
              Book Now <i className="bi bi-arrow-right ms-1"></i>
            </button>
          )}
          {/* <button type="button" className="fr-details-btn" aria-expanded={showDetails} onClick={() => setShowDetails(!showDetails)}>
            {showDetails ? "Hide Details" : "View Details"}
          </button> */}
        </div>
      </div>

     <style>{`
     {/* .flight-summary {
  display: flex;
  align-items: left;
  justify-content: right;
  padding: 10px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f8faff;
  border-radius: 8px;
}

.flight-summary-items {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
}

.flight-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 14px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.baggage-badge,.availability-badge {
  color: #4546ad;
  background: #eef1ff;
}

.refund-badge {
  color: #d94335;
  background: #fff0ef;
}

@media (max-width: 576px) {
  .flight-summary-items {
    justify-content: flex-start;
    gap: 8px;
  }

  .flight-badge {
    padding: 7px 10px;
    font-size: 12px;
  }
} */}
.flight-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border: 1px solid #dce5f0;
  border-radius: 8px;
  background: #f8faff;
}

.flight-details-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 9px 14px;
  border: 1px solid #e0e7ff;
  border-radius: 24px;
  background: #eef1ff;
  color: #3658b5;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.flight-details-btn i {
  font-size: 11px;
  transition: transform 0.2s ease;
}

.flight-details-btn:hover {
  background: #e4eaff;
}

.flight-summary-items {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.flight-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 14px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.baggage-badge,
.availability-badge {
  color: #4546ad;
  background: #eef1ff;
}

.refund-badge {
  color: #d94335;
  background: #fff0ef;
}

@media (max-width: 768px) {
  .flight-summary {
    flex-wrap: wrap;
    padding: 12px;
  }

  .flight-summary-items {
    justify-content: flex-start;
    width: 100%;
  }
}
    `}</style>
{/* <div className="fr-row flight-summary">  
  <div className="flight-summary-items">
    <div className="flight-badge baggage-badge">
      <span><i class="bi bi-suitcase2"></i></span> 1 piece, up to {fare?.baggage_policy} KG each
    </div>

    <div className="flight-badge refund-badge">
      <span><i class="bi bi-x-circle-fill"></i></span> 
      {fare.is_refundable ? "Refundable fare" : "Non Refundable, Non Changable"}
    </div>

     <div className="flight-badge availability-badge">
      Seats Available: {fare?.available_seats}
    </div>
  </div>
</div> */}

<div className="fr-row flight-summary">
  <button className="flight-details-btn" type="button" aria-expanded={showDetails} onClick={() => setShowDetails(!showDetails)}>
    <span>{showDetails ? "Hide" : "Flight"} Details</span>
    <i className="bi bi-chevron-down"></i>
  </button>

  <div className="flight-summary-items">
    <div className="flight-badge baggage-badge">
      <i className="bi bi-suitcase2"></i>
      1 piece, up to {fare?.baggage_policy} KG each
    </div>

    <div className="flight-badge refund-badge">
      <i className="bi bi-x-circle-fill"></i>
      {fare.is_refundable
        ? "Refundable fare"
        : "Non Refundable, Non Changable"}
    </div>

    <div className="flight-badge availability-badge">
      Seats Available: {fare?.available_seats}
    </div>
  </div>
</div>



      {showDetails && (
        <div className="fr-details">
          <div className="fr-tabs" role="tablist">
            {DETAIL_TABS.map((t) => (
              <button
                type="button"
                role="tab"
                key={t.value}
                aria-selected={tab == t.value}
                className={`fr-tab ${tab == t.value ? "active" : ""}`}
                onClick={() => setTab(t.value)}
              >
                <i className={`bi ${t.icon}`}></i> {t.label}
              </button>
            ))}
          </div>

          {tab == "flight" && (
            <div>
              {/* <div className="fr-chips">
                <span className="fr-chip"><i className="bi bi-airplane"></i>{fare.airline?.airline_name} {fare.flight_number}</span>
                <span className="fr-chip text-capitalize"><i className="bi bi-person-badge"></i>{fare.cabin_class}{fare.rbd ? ` (${fare.rbd})` : ""}</span>
                <span className="fr-chip"><i className="bi bi-clock"></i>{formatDuration(duration)} · {stops == 0 ? "Non-stop" : `${stops} Stop${stops > 1 ? "s" : ""}`}</span>
                {fare.baggage_policy && <span className="fr-chip"><i className="bi bi-suitcase2"></i>{fare.baggage_policy}</span>}
              </div> */}
              <div className="row g-3">
              <div className="col-12 col-md-6">
              <div className="fr-detail-title">Base Fare Breakup</div>
              <div className="table-responsive">
                <table className="table table-sm mb-0 fr-table">
                  <thead>
                    <tr>
                      <th>Passenger</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakupRows.map((r) => {                      
                      const f = fare.fare?.[r.key];
                      const amt = (v) => (f ? formatINR(v) : "-");
                      return (
                        <tr key={r.key}>
                          <td>{r.label}</td>
                          <td className="text-end">{amt(f?.total_fare)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </div>
              <div className="col-12 col-md-6">
              <div className="fr-detail-title">Total Fare Summary</div>
              <div className="table-responsive">
                <table className="table table-sm mb-0 fr-table">
                  <thead>
                    <tr>
                      <th>Passenger</th>
                      <th className="text-end">Per Passenger</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.map((r) => {
                      const f = fare.fare?.[r.key];
                      const amt = (v) => (f ? formatINR(v) : "-");
                      return (
                        <tr key={r.key}>
                          <td>{r.label} x {r.count}</td>
                          <td className="text-end">{amt(f?.total_fare)}</td>
                          <td className="text-end fw-bold">{formatINR((f?.total_fare || 0) * r.count)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </div>
              </div>
            </div>
          )}

          {tab == "baggage" && (
            <div>
              <div className="fr-detail-title">Baggage Allowance</div>
              {fare.baggage_policy ? (
                <div className="fr-banner info">
                  {/* <i className="bi bi-suitcase2"></i>
                  <div>
                    <div className="fr-banner-title">{fare.baggage_policy}</div>
                    <div className="fr-muted">
                      {fare.airline?.airline_name} {fare.flight_number} &middot; <span className="text-capitalize">{fare.cabin_class}</span>
                      {fare.rbd ? ` (${fare.rbd})` : ""}
                    </div>
                  </div> */}
                  <div className="table-responsive">
                <table className="table table-sm mb-0 fr-table">
                  <thead>
                    <tr>
                      <th>Sector / Flight</th>
                      <th>Passenger Type</th>
                      <th>Check-In Baggage</th>
                      <th>Cabin Baggage</th>
                    </tr>
                  </thead>
                  <tbody>
                     <tr>
                         <td style={{fontSize: 14,fontWeight: 700,}}>{fare?.airline?.airline_name} {fare?.flight_number} ({fare?.origin?.airport_code}-{fare?.destination?.airport_code})</td>
                         <td style={{fontSize: 14,fontWeight: 700,}}>Adult</td>
                         <td style={{fontSize: 14, color: "#008eff", fontWeight: 700,}}>{`1 piece, up to ${fare?.baggage_policy} KG each`}</td>
                         <td style={{fontSize: 14, color: "var(--btn-bg)", fontWeight: 700,}}>{`7 KG`}</td>
                      </tr>
                       <tr>
                         <td style={{fontSize: 14,fontWeight: 700,}}>{fare?.airline?.airline_name} {fare?.flight_number} ({fare?.origin?.airport_code}-{fare?.destination?.airport_code})</td>
                         <td style={{fontSize: 14,fontWeight: 700,}}>Child</td>
                         <td style={{fontSize: 14, color: "#008eff", fontWeight: 700,}}>{`1 piece, up to ${fare?.baggage_policy} KG each`}</td>
                         <td style={{fontSize: 14, color: "var(--btn-bg)", fontWeight: 700,}}>{`7 KG`}</td>
                      </tr>
                       <tr>
                         <td style={{fontSize: 14,fontWeight: 700,}}>{fare?.airline?.airline_name} {fare?.flight_number} ({fare?.origin?.airport_code}-{fare?.destination?.airport_code})</td>
                         <td style={{fontSize: 14,fontWeight: 700,}}>Infant</td>
                         <td style={{fontSize: 14, color: "#008eff", fontWeight: 700,}}>{`0 PC`}</td>
                         <td style={{fontSize: 14, color: "var(--btn-bg)", fontWeight: 700,}}>{`7 KG`}</td>
                      </tr>
                  </tbody>
                </table>
              </div>
                </div>
              ) : (
                <div className="fr-detail-text">Baggage details are not available for this fare.</div>
              )}
            </div>
          )}

          {tab == "cancellation" && (
            <div>
            <div className="fr-detail-title">Cancellation Policy</div>
              <div className={`fr-banner ${fare.is_refundable ? "yes" : "no"}`}>
                <i className={`bi ${fare.is_refundable ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}></i>
                <div>
                  <div className="fr-banner-title">{fare.is_refundable ? "Refundable fare" : "Non Refundable, Non Changable"}</div>
                  <div className="fr-muted">{fare.is_refundable ? "This fare can be cancelled as per the rules below." : "This fare is non-refundable and cannot be changed once booked."}</div>
                </div>
              </div>
              {/* <div className="fr-detail-title mt-3">Fare Rules</div>
              <div className="fr-detail-text">{fare.fare_rule || "-"}</div> */}
            </div>
          )}

          {tab == "itinerary" && (
            <div>
              <div className="fr-detail-title">Itinerary</div>
              <div className="fr-legs">
                {(fare.stops?.length ? fare.stops : [{
                  stop_number: 1,
                  departure_airport_code: fare.origin?.airport_code,
                  departure_time: fare.origin?.time,
                  departure_terminal: fare.origin?.terminal,
                  arrival_airport_code: fare.destination?.airport_code,
                  arrival_time: fare.destination?.time,
                  arrival_terminal: fare.destination?.terminal,
                }]).map((leg) => (
                  <div className="fr-leg" key={leg.stop_number}>
                    <span className="fr-leg-end">
                      <b>{leg.departure_airport_code}</b> {formatTime(leg.departure_time)}
                      {leg.departure_terminal ? <span className="fr-muted"> T{leg.departure_terminal}</span> : null}
                    </span>
                    <i className="bi bi-arrow-right"></i>
                    <span className="fr-leg-end">
                      <b>{leg.arrival_airport_code}</b> {formatTime(leg.arrival_time)}
                      {leg.arrival_terminal ? <span className="fr-muted"> T{leg.arrival_terminal}</span> : null}
                    </span>
                  </div>
                ))}
              </div>
              <div className="fr-meta">
                <span>Fare Code <b>{fare.fare_code}</b></span>
                <span>Supplier <b>{fare.supplier_code}</b> <span className="text-capitalize">({fare.supplier_type})</span></span>
                <span>Ref <b>{fare.fare_reference_code}</b></span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FlightResultCard;
